import './bar-chart.css';

export interface Bar {
  label: string;
  value: number;
  /** Shown on hover and read out; already formatted, e.g. "£650". */
  formatted: string;
}

export interface BarChartProps {
  bars: Bar[];
  /** Describes the whole chart to a screen reader. */
  caption: string;
  /** Peak line, e.g. "Peak 15:00 — £650". */
  summary?: string;
  height?: number;
  emptyMessage?: string;
}

/**
 * The by-hour and by-day bars.
 *
 * Deliberately not a charting library: it is one series of positive values with no
 * axes, and a dependency for that costs more than it saves.
 *
 * Accessibility is where hand-rolled charts usually fail, so: the whole thing is a
 * table to a screen reader (`role="table"` with a row per bar), each bar carries its
 * formatted value, and the summary line states the peak in text rather than leaving it
 * to be read off the picture.
 */
export function BarChart({ bars, caption, summary, height = 140, emptyMessage }: BarChartProps) {
  if (bars.length === 0) {
    return <p className="barChartEmpty">{emptyMessage ?? 'No activity in this period.'}</p>;
  }

  // Scaled to the peak, not to a fixed maximum: a quiet day should still show shape
  // rather than a row of slivers.
  const peak = Math.max(...bars.map((bar) => bar.value), 1);

  return (
    <figure className="barChart" style={{ '--chart-height': `${height}px` } as React.CSSProperties}>
      <div className="barChartPlot" role="table" aria-label={caption}>
        {bars.map((bar) => (
          <div key={bar.label} className="barChartColumn" role="row">
            <span className="visuallyHidden" role="cell">
              {bar.label}: {bar.formatted}
            </span>
            <span
              className="barChartBar"
              // A floor only for a bar with something in it. A blanket `min-height` in
              // CSS cannot tell the two apart, so every empty hour drew a stub and a
              // quiet day looked like a full one; without any floor, one session
              // against a peak of a few hundred draws a 1px line that reads as part of
              // the axis rather than as a measurement.
              style={{
                height: `${Math.round((bar.value / peak) * 100)}%`,
                ...(bar.value > 0 ? { minHeight: '4px' } : {}),
              }}
              title={`${bar.label} — ${bar.formatted}`}
              aria-hidden="true"
            />
          </div>
        ))}
      </div>

      <div className="barChartAxis" aria-hidden="true">
        {bars.map((bar) => (
          <span key={bar.label} className="barChartTick">
            {bar.label}
          </span>
        ))}
      </div>

      {summary ? <figcaption className="barChartSummary">{summary}</figcaption> : null}
    </figure>
  );
}
